'use client'

import * as React from 'react'
import { Dialog as SheetPrimitive } from '@base-ui/react/dialog'

import { cn } from '@repo/ui/lib/utils'
import { Button } from '@repo/ui/components/ui/button'
import { DrawerPortalContainerBoundary } from '@repo/ui/components/ui/drawer-portal-context'
import { XIcon } from 'lucide-react'

function Sheet({ ...props }: SheetPrimitive.Root.Props) {
  return <SheetPrimitive.Root data-slot="sheet" {...props} data-sentry-element="SheetPrimitive.Root" data-sentry-component="Sheet" data-sentry-source-file="sheet.tsx" />
}

function SheetClose({ ...props }: SheetPrimitive.Close.Props) {
  return <SheetPrimitive.Close data-slot="sheet-close" {...props} data-sentry-element="SheetPrimitive.Close" data-sentry-component="SheetClose" data-sentry-source-file="sheet.tsx" />
}

function SheetContent({
  children,
  className,
  forceOverlay = false,
  overlayClassName,
  showCloseButton = true,
  side = 'right',
  ...props
}: SheetPrimitive.Popup.Props & {
  forceOverlay?: boolean
  overlayClassName?: string
  showCloseButton?: boolean
  side?: 'bottom' | 'left' | 'right' | 'top'
}) {
  return (
    <SheetPortal data-sentry-element="SheetPortal" data-sentry-component="SheetContent" data-sentry-source-file="sheet.tsx">
      <SheetOverlay className={overlayClassName} forceRender={forceOverlay} data-sentry-element="SheetOverlay" data-sentry-source-file="sheet.tsx" />
      <SheetPrimitive.Popup
        className={cn(
          'fixed z-60 flex flex-col gap-4 bg-popover bg-clip-padding text-sm text-popover-foreground shadow-lg transition duration-200 ease-drawer data-ending-style:opacity-0 data-starting-style:opacity-0 data-[side=bottom]:inset-x-0 data-[side=bottom]:bottom-0 data-[side=bottom]:h-auto data-[side=bottom]:border-t data-[side=bottom]:data-ending-style:translate-y-[2.5rem] data-[side=bottom]:data-starting-style:translate-y-[2.5rem] data-[side=left]:inset-y-0 data-[side=left]:left-0 data-[side=left]:h-full data-[side=left]:w-3/4 data-[side=left]:border-r data-[side=left]:data-ending-style:translate-x-[-2.5rem] data-[side=left]:data-starting-style:translate-x-[-2.5rem] data-[side=right]:inset-y-0 data-[side=right]:right-0 data-[side=right]:h-full data-[side=right]:w-3/4 data-[side=right]:border-l data-[side=right]:data-ending-style:translate-x-[2.5rem] data-[side=right]:data-starting-style:translate-x-[2.5rem] data-[side=top]:inset-x-0 data-[side=top]:top-0 data-[side=top]:h-auto data-[side=top]:border-b data-[side=top]:data-ending-style:translate-y-[-2.5rem] data-[side=top]:data-starting-style:translate-y-[-2.5rem] data-[side=left]:sm:max-w-sm data-[side=right]:sm:max-w-sm',
          className,
        )}
        data-side={side}
        data-slot="sheet-content"
        {...props}
      data-sentry-element="SheetPrimitive.Popup" data-sentry-source-file="sheet.tsx">
        <DrawerPortalContainerBoundary data-sentry-element="DrawerPortalContainerBoundary" data-sentry-source-file="sheet.tsx">{children}</DrawerPortalContainerBoundary>
        {showCloseButton && (
          <SheetPrimitive.Close
            data-slot="sheet-close"
            render={<Button className="absolute top-3 right-3" size="icon-sm" variant="ghost" />}
          >
            <XIcon />
            <span className="sr-only">Close</span>
          </SheetPrimitive.Close>
        )}
      </SheetPrimitive.Popup>
    </SheetPortal>
  )
}

function SheetDescription({ className, ...props }: SheetPrimitive.Description.Props) {
  return (
    <SheetPrimitive.Description
      className={cn('text-sm text-muted-foreground', className)}
      data-slot="sheet-description"
      {...props}
    data-sentry-element="SheetPrimitive.Description" data-sentry-component="SheetDescription" data-sentry-source-file="sheet.tsx" />
  )
}

function SheetFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      className={cn('mt-auto flex flex-col gap-2 p-4', className)}
      data-slot="sheet-footer"
      {...props}
    data-sentry-component="SheetFooter" data-sentry-source-file="sheet.tsx" />
  )
}

function SheetHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      className={cn('flex flex-col gap-0.5 p-4', className)}
      data-slot="sheet-header"
      {...props}
    data-sentry-component="SheetHeader" data-sentry-source-file="sheet.tsx" />
  )
}

function SheetOverlay({ className, ...props }: SheetPrimitive.Backdrop.Props) {
  return (
    <SheetPrimitive.Backdrop
      className={cn(
        'fixed inset-0 z-60 bg-black/10 transition-opacity duration-150 data-ending-style:opacity-0 data-starting-style:opacity-0 supports-backdrop-filter:backdrop-blur-xs',
        className,
      )}
      data-slot="sheet-overlay"
      {...props}
    data-sentry-element="SheetPrimitive.Backdrop" data-sentry-component="SheetOverlay" data-sentry-source-file="sheet.tsx" />
  )
}

function SheetPortal({ ...props }: SheetPrimitive.Portal.Props) {
  return <SheetPrimitive.Portal data-slot="sheet-portal" {...props} data-sentry-element="SheetPrimitive.Portal" data-sentry-component="SheetPortal" data-sentry-source-file="sheet.tsx" />
}

function SheetTitle({ className, ...props }: SheetPrimitive.Title.Props) {
  return (
    <SheetPrimitive.Title
      className={cn('text-base font-medium text-foreground', className)}
      data-slot="sheet-title"
      {...props}
    data-sentry-element="SheetPrimitive.Title" data-sentry-component="SheetTitle" data-sentry-source-file="sheet.tsx" />
  )
}

function SheetTrigger({ ...props }: SheetPrimitive.Trigger.Props) {
  return <SheetPrimitive.Trigger data-slot="sheet-trigger" {...props} data-sentry-element="SheetPrimitive.Trigger" data-sentry-component="SheetTrigger" data-sentry-source-file="sheet.tsx" />
}

export {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
}
